import { html, attr, Fragment, Async, prop, Signal } from '@tempots/dom'
import { type Controller } from '@tempots/beatui'
import { PageLayout } from '@/components/layout'
import { LoadingState } from '@/components/ui/loading-state'
import { ErrorState } from '@/components/ui/error-state'
import { useActionDetail } from './hooks/use-action-detail'
import { ActionHeader } from './components/action-header'
import { ActionDescription } from './components/action-description'
import { ActionPricing } from './components/action-pricing'
import { TryItPanel } from './components/try-it-panel'
import { Workspace } from '@/state/workspace'

export interface ActionDetailPageOptions {
  projectId: Signal<string>
  id: Signal<string>
}

export function ActionDetailPage({ projectId, id }: ActionDetailPageOptions) {
  projectId.on(Workspace.changeProject)

  // Initialize state and actions
  const {
    action,
    costEstimate,
    calculating,
    executing,
    executeResult,
    loadData,
    calculateCost,
    executeAction,
  } = useActionDetail(id)

  // Controller reference for form access
  const controllerRef: { current: Controller<unknown> | undefined } = {
    current: undefined,
  }

  // Create the promise for the initial load
  const dataPromise = loadData()

  return PageLayout({
    header: ActionHeader({ actionName: id, action }),
    content: Async(dataPromise, {
      pending: () => LoadingState({ message: 'Loading action...', size: 'lg' }),
      error: (err) =>
        ErrorState({
          error: prop<string | null>(
            err instanceof Error ? err.message : 'Failed to load action'
          ),
        }),
      then: () =>
        Fragment(
          // Left: Documentation
          html.div(
            attr.class('flex flex-row flex-1 space-y-6 gap-6 justify-between'),
            ActionDescription({ action }),
            ActionPricing({ action })
          ),
          // Right: Try It Panel
          TryItPanel({
            action,
            costEstimate,
            calculating,
            executing,
            executeResult,
            onCalculateCost: calculateCost,
            onExecute: executeAction,
            controllerRef,
          })
        ),
    }),
  })
}
